<template>
  <transition name="toolbar-fade">
    <div
      v-if="visible && annotation && position && position.x !== undefined"
      class="contextual-field-toolbar"
      :style="toolbarStyle"
      @click.stop
    >
      <!-- Toolbar Header -->
      <div class="toolbar-header">
        <div class="field-info">
          <span class="field-icon" :class="fieldIconClass">
            <i :class="fieldIcon"></i>
          </span>
          <input
            v-model="localAnnotation.fieldName"
            @blur="validateAndUpdate"
            class="field-name-input"
            placeholder="Field name"
          />
        </div>
        <div class="toolbar-actions">
          <button
            class="btn-toolbar duplicate-btn"
            @click="duplicateField"
            title="Duplicate field"
          >
            <i class="fa fa-copy"></i>
          </button>
          <button
            class="btn-toolbar delete-btn"
            @click="deleteField"
            title="Delete field"
          >
            <i class="fa fa-trash"></i>
          </button>
          <button
            class="btn-toolbar close-btn"
            @click="$emit('close')"
            title="Close (Esc)"
          >
            <i class="fa fa-times"></i>
          </button>
        </div>
      </div>

      <!-- Icon Buttons Row -->
      <div class="toolbar-icon-buttons">
        <button
          class="btn-icon"
          @click="openModal('basic')"
          title="Basic properties"
        >
          <i class="fa fa-info-circle"></i>
          <span>Basic</span>
        </button>
        <button
          class="btn-icon"
          @click="openModal('properties')"
          title="Field properties"
        >
          <i class="fa fa-sliders-h"></i>
          <span>Properties</span>
        </button>
        <button
          class="btn-icon"
          @click="openModal('position')"
          title="Position & size"
        >
          <i class="fa fa-arrows-alt"></i>
          <span>Position</span>
        </button>
      </div>

      <!-- Pointer Arrow -->
      <div
        class="toolbar-pointer"
        :class="pointerPlacement"
        :style="{ left: pointerOffset + 'px' }"
      ></div>
    </div>
  </transition>
</template>

<script>
import Vue from 'vue';

export default {
  name: 'ContextualFieldToolbar',
  props: {
    annotation: {
      type: Object,
      default: null
    },
    position: {
      type: Object,
      default: null
    },
    scale: {
      type: Number,
      default: 1
    },
    allAnnotations: {
      type: Array,
      default: () => []
    },
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      localAnnotation: null,
      toolbarElement: null,
      toolbarPosition: { x: 0, y: 0 },
      pointerPlacement: 'bottom',
      pointerOffset: 0,
      cachedToolbarWidth: 380,
      cachedToolbarHeight: 120
    };
  },
  computed: {
    fieldIcon() {
      const iconMap = {
        signature: 'fa fa-signature',
        text: 'fa fa-font',
        select: 'fa fa-list',
        date: 'fa fa-calendar'
      };
      return iconMap[this.annotation?.type] || 'fa fa-square';
    },
    fieldIconClass() {
      const classMap = {
        signature: 'icon-signature',
        text: 'icon-text',
        select: 'icon-select',
        date: 'icon-date'
      };
      return classMap[this.annotation?.type] || '';
    },
    toolbarStyle() {
      if (!this.position) {
        return {
          display: 'none'  // Hide toolbar when position is not available
        };
      }

      // Calculate toolbar position
      const { x, y, placement } = this.calculateToolbarPosition();

      return {
        position: 'fixed',
        left: x + 'px',
        top: y + 'px',
        zIndex: 1000
      };
    }
  },
  watch: {
    annotation: {
      handler(newAnnotation) {
        if (newAnnotation) {
          this.localAnnotation = this.cloneAnnotation(newAnnotation);
        } else {
          this.localAnnotation = null;
        }
      },
      immediate: true
    },
    visible(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          this.updatePosition();
          this.addEventListeners();
        });
      } else {
        this.removeEventListeners();
      }
    },
    position: {
      handler(newPosition, oldPosition) {
        console.log('[ContextualFieldToolbar] Position prop changed:', {
          old: oldPosition,
          new: newPosition
        });
        this.$nextTick(() => {
          this.updatePosition();
        });
      },
      deep: true
    }
  },
  mounted() {
    // Cache toolbar dimensions after mounting
    this.$nextTick(() => {
      if (this.$el && this.$el.offsetWidth > 0) {
        this.cachedToolbarWidth = this.$el.offsetWidth;
        this.cachedToolbarHeight = this.$el.offsetHeight;
        console.log('[ContextualFieldToolbar] Cached toolbar dimensions:', {
          width: this.cachedToolbarWidth,
          height: this.cachedToolbarHeight
        });
      }
    });

    if (this.visible) {
      this.addEventListeners();
    }
  },
  beforeDestroy() {
    this.removeEventListeners();
  },
  methods: {
    cloneAnnotation(annotation) {
      if (!annotation) return null;

      return {
        ...annotation,
        position: { ...annotation.position },
        properties: this.cloneProperties(annotation.properties),
        assignableTo: [...(annotation.assignableTo || [])],
        signee_type: annotation.signee_type || ''
      };
    },

    cloneProperties(properties) {
      if (!properties) return {};

      const cloned = { ...properties };

      // Deep clone options array for select fields
      if (cloned.options && Array.isArray(cloned.options)) {
        cloned.options = cloned.options.map(option => ({ ...option }));
      }

      return cloned;
    },

    calculateToolbarPosition() {
      if (!this.position) {
        return { x: 0, y: 0, placement: 'bottom' };
      }

      const OFFSET = 10;
      const POINTER_SIZE = 8;
      const MIN_EDGE_DISTANCE = 10;

      // Use cached dimensions if toolbar element isn't available yet
      const toolbar = this.$el;
      let toolbarWidth = this.cachedToolbarWidth;
      let toolbarHeight = this.cachedToolbarHeight;

      // Update cached dimensions if element is available
      if (toolbar && toolbar.offsetWidth > 0) {
        toolbarWidth = toolbar.offsetWidth;
        toolbarHeight = toolbar.offsetHeight;
        this.cachedToolbarWidth = toolbarWidth;
        this.cachedToolbarHeight = toolbarHeight;
      }

      // Get viewport bounds (use document.documentElement for better compatibility)
      const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

      // Get current scroll position
      const scrollX = window.pageXOffset || document.documentElement.scrollLeft || 0;
      const scrollY = window.pageYOffset || document.documentElement.scrollTop || 0;

      console.log('[ContextualFieldToolbar] calculateToolbarPosition inputs:', {
        position: this.position,
        toolbarDimensions: { width: toolbarWidth, height: toolbarHeight },
        viewport: { width: viewportWidth, height: viewportHeight },
        scroll: { x: scrollX, y: scrollY }
      });

      // Calculate preferred position (below field to keep field visible)
      let x = this.position.x + (this.position.width / 2) - (toolbarWidth / 2);
      let y = this.position.y + this.position.height + OFFSET + POINTER_SIZE;
      let placement = 'top'; // Pointer placement at top, pointing up to field

      // Check if toolbar would go off bottom edge
      if (y + toolbarHeight > viewportHeight - MIN_EDGE_DISTANCE) {
        // Place above field instead
        y = this.position.y - toolbarHeight - OFFSET - POINTER_SIZE;
        placement = 'bottom';

        // If still off screen at top, place at top edge
        if (y < MIN_EDGE_DISTANCE) {
          y = MIN_EDGE_DISTANCE;
        }
      }

      // Horizontal edge detection
      if (x < MIN_EDGE_DISTANCE) {
        x = MIN_EDGE_DISTANCE;
      }
      if (x + toolbarWidth > viewportWidth - MIN_EDGE_DISTANCE) {
        x = viewportWidth - toolbarWidth - MIN_EDGE_DISTANCE;
      }

      // Calculate pointer offset (relative to toolbar left edge)
      this.pointerOffset = this.position.x + (this.position.width / 2) - x;

      // Constrain pointer to toolbar bounds
      const minPointer = POINTER_SIZE;
      const maxPointer = toolbarWidth - POINTER_SIZE;
      this.pointerOffset = Math.max(minPointer, Math.min(maxPointer, this.pointerOffset));

      this.pointerPlacement = placement;

      console.log('[ContextualFieldToolbar] calculateToolbarPosition result:', {
        x, y, placement,
        pointerOffset: this.pointerOffset,
        pointerPlacement: this.pointerPlacement
      });

      return { x, y, placement };
    },

    updatePosition() {
      if (!this.visible || !this.position) {
        console.log('[ContextualFieldToolbar] Cannot update position - missing data:', {
          visible: this.visible,
          hasPosition: !!this.position
        });
        return;
      }

      // Recalculate position
      const newPosition = this.calculateToolbarPosition();
      console.log('[ContextualFieldToolbar] Position updated:', newPosition);

      // Force Vue to re-render with new position
      this.$forceUpdate();
    },

    addEventListeners() {
      document.addEventListener('click', this.handleDocumentClick);
      document.addEventListener('keydown', this.handleKeyDown);
      window.addEventListener('resize', this.handleResize);
      window.addEventListener('scroll', this.handleScroll, true);
    },

    removeEventListeners() {
      document.removeEventListener('click', this.handleDocumentClick);
      document.removeEventListener('keydown', this.handleKeyDown);
      window.removeEventListener('resize', this.handleResize);
      window.removeEventListener('scroll', this.handleScroll, true);
    },

    handleDocumentClick(event) {
      // Don't close if modal is open (check for modal overlay or modal container)
      const isModalClick = event.target.closest('.modal-overlay, .field-property-modal');
      if (isModalClick) {
        console.log('[ContextualFieldToolbar] Click was on modal, not closing toolbar');
        return;
      }

      // Check if click is outside toolbar
      if (this.$el && !this.$el.contains(event.target)) {
        // Check if click is on the field itself (don't close in that case)
        const fieldElement = document.querySelector(`[data-annotation-id="${this.annotation?.id}"]`);
        if (fieldElement && !fieldElement.contains(event.target)) {
          this.$emit('close');
        }
      }
    },

    handleKeyDown(event) {
      if (event.key === 'Escape') {
        // Don't close toolbar if modal is open (modal will handle its own Escape key)
        const isModalOpen = document.querySelector('.modal-overlay');
        if (isModalOpen) {
          console.log('[ContextualFieldToolbar] Modal is open, not closing toolbar on Escape');
          return;
        }
        this.$emit('close');
      }
    },

    handleResize() {
      this.updatePosition();
    },

    handleScroll() {
      // Update position or hide toolbar on scroll
      this.updatePosition();
    },

    updateAnnotation() {
      if (this.localAnnotation) {
        this.$emit('update', this.localAnnotation);
      }
    },

    validateAndUpdate() {
      if (this.localAnnotation) {
        // Validate field name is not empty and is unique
        if (!this.localAnnotation.fieldName.trim()) {
          this.localAnnotation.fieldName = `field_${Date.now()}`;
        }
        this.$emit('validate-field-name', this.localAnnotation);
        this.updateAnnotation();
      }
    },

    deleteField() {
      if (this.annotation && confirm('Are you sure you want to delete this field?')) {
        this.$emit('delete', this.annotation.id);
      }
    },

    duplicateField() {
      if (this.annotation) {
        this.$emit('duplicate', this.annotation.id);
      }
    },

    openModal(tab) {
      this.$emit('open-modal', { annotation: this.localAnnotation, activeTab: tab });
    }
  }
};
</script>

<style scoped>
.contextual-field-toolbar {
  background-color: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  width: auto;
  min-width: 380px;
  font-size: 13px;
}

/* Toolbar Header */
.toolbar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 14px;
  border-bottom: 1px solid #e2e8f0;
  background-color: #f7fafc;
  border-radius: 8px 8px 0 0;
}

.field-info {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
}

.field-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  font-size: 14px;
}

.icon-signature {
  background-color: rgba(66, 153, 225, 0.1);
  color: #4299e1;
}

.icon-text {
  background-color: rgba(72, 187, 120, 0.1);
  color: #48bb78;
}

.icon-select {
  background-color: rgba(159, 122, 234, 0.1);
  color: #9f7aea;
}

.icon-date {
  background-color: rgba(237, 137, 54, 0.1);
  color: #ed8936;
}

.field-name-input {
  flex: 1;
  border: none;
  background: transparent;
  font-size: 14px;
  font-weight: 600;
  color: #2d3748;
  padding: 4px 8px;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.field-name-input:hover {
  background-color: rgba(0, 0, 0, 0.02);
}

.field-name-input:focus {
  outline: none;
  background-color: #fff;
  box-shadow: 0 0 0 2px rgba(66, 153, 225, 0.2);
}

.toolbar-actions {
  display: flex;
  gap: 6px;
}

.btn-toolbar {
  background: transparent;
  border: 1px solid transparent;
  border-radius: 4px;
  padding: 6px 8px;
  cursor: pointer;
  transition: all 0.2s;
  color: #4a5568;
}

.btn-toolbar:hover {
  background-color: rgba(0, 0, 0, 0.04);
  border-color: rgba(0, 0, 0, 0.08);
}

.btn-toolbar.delete-btn:hover {
  background-color: rgba(245, 101, 101, 0.1);
  border-color: rgba(245, 101, 101, 0.3);
  color: #f56565;
}

.btn-toolbar.duplicate-btn:hover {
  background-color: rgba(66, 153, 225, 0.1);
  border-color: rgba(66, 153, 225, 0.3);
  color: #4299e1;
}

.btn-toolbar.close-btn:hover {
  background-color: rgba(113, 128, 150, 0.1);
  border-color: rgba(113, 128, 150, 0.3);
  color: #718096;
}

.btn-toolbar.small {
  padding: 2px 6px;
  font-size: 12px;
}

/* Icon Buttons Row */
.toolbar-icon-buttons {
  display: flex;
  gap: 8px;
  padding: 10px 14px;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
  background-color: #fff;
}

.btn-icon {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  background-color: #f7fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 10px 12px;
  cursor: pointer;
  transition: all 0.2s;
  color: #4a5568;
}

.btn-icon i {
  font-size: 18px;
}

.btn-icon span {
  font-size: 11px;
  font-weight: 500;
}

.btn-icon:hover {
  background-color: rgba(66, 153, 225, 0.1);
  border-color: rgba(66, 153, 225, 0.3);
  color: #4299e1;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* Pointer Arrow */
.toolbar-pointer {
  position: absolute;
  width: 12px;
  height: 12px;
  background-color: #fff;
  border: 1px solid #e2e8f0;
  transform: rotate(45deg);
  z-index: -1;
}

.toolbar-pointer.bottom {
  bottom: -6px;
  border-top: none;
  border-left: none;
}

.toolbar-pointer.top {
  top: -6px;
  border-bottom: none;
  border-right: none;
}

/* Transitions */
.toolbar-fade-enter-active,
.toolbar-fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.toolbar-fade-enter,
.toolbar-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* Scrollbar styling */
.toolbar-content::-webkit-scrollbar {
  width: 6px;
}

.toolbar-content::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.toolbar-content::-webkit-scrollbar-thumb {
  background: #cbd5e0;
  border-radius: 3px;
}

.toolbar-content::-webkit-scrollbar-thumb:hover {
  background: #a0aec0;
}
</style>